import { CheckBoxWithLabelProps, CheckBoxWithLabelWidget, TabStyles } from '@tableau/widgets';
import { InputAttrs } from '@tableau/widgets/lib/src/utils/HtmlAttrs';
import { useField } from 'formik';
import React from 'react';
import { style } from 'typestyle';
import _ from 'underscore';

export type FormikCheckboxProps = {
  name: string;
  label: string;
  testId: string;
  className?: string;
  beforeChange?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<any>;
  alwaysShowChildren?: boolean;
  childrenSkipIndent?: boolean;
}

const indentedPadding = style({
  paddingTop: 2 * TabStyles.Spacing.SmallSpace,
  paddingLeft: 2 * TabStyles.Spacing.MediumSpace
});

/**
 * A checkbox using {@link CheckBoxWithLabelWidget} which automatically wires to a boolean formik field using the
 * {@code name} prop. Nested child components are automatically hidden when the checkbox is unchecked.
 */

export const FormikCheckbox: React.FunctionComponent<FormikCheckboxProps & Partial<CheckBoxWithLabelProps> & InputAttrs> =
function FormikCheckbox(props) {
  const [ field ] = useField({name: props.name, type: 'checkbox'});
  // spread the props last, which will let callers override checked and onChange by providing them as props
  const { beforeChange, alwaysShowChildren, childrenSkipIndent, checked, ...otherProps } = props;

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // For some reason using event.persist() wasn't returning the right value
      // React's synthetic event requires the key's be explicitly referenced.
      const originalTarget = _.pick(event.target, 'name', 'type', 'checked', 'value');
      if (beforeChange) {
        beforeChange(event).then(() => {
          field.onChange({ target: originalTarget });
        }).catch(() => { /* no-op for now */ });
      } else {
        field.onChange(event);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [beforeChange, field?.onChange]
  );

  return (
    <>
      <CheckBoxWithLabelWidget
        checked={props.checked ?? field.checked}
        onChange={handleChange}
        {...otherProps}
        // need to set value to the string 'true' or 'false' in order for formik to behave when initial value is undefined
        // this is probably a bug in formik that needs to be addressed.
        // Also take props.checked into account to override the formik value if needed
        value={(props.checked ?? field.checked) ? 'true' : 'false'}
      />
      {
        (props.alwaysShowChildren || (props.checked ?? field.checked)) && props.children &&
          <div className={props.childrenSkipIndent ? '' : indentedPadding}>
            {props.children}
          </div>
      }
    </>
  );
};
